import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { memo, useEffect, useMemo, useState } from 'react'
import { useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import axiosInstance from '../../api/axiosInstance'
import DataTable from '../../components/data-table/DataTable'
import { DataTableColumnHeader } from '../../components/data-table/DataTableColumnHeader'
import DataTableShimmer from '../../components/data-table/DataTableShimmer'
import DataTableRowActions from '../../components/my-forms/DataTableRowActions'
// import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/checkbox'
import { Switch } from '../../components/ui/switch'
import { AuthContext } from '../../contexts/auth-context'
import { useCreateData, useUpdateData } from '../../hooks/useApi'
import useTitle from '../../hooks/useTitle'
import logger from '../../utils/logger'
import NotFound from '../not-found'

dayjs.extend(relativeTime)

function MyForms() {
  const { auth } = useContext(AuthContext)

  const userId = auth.user._id
  const [forms, setForms] = useState([])
  const [totalEntries, setTotalEntries] = useState(0)
  const [refresh, setRefresh] = useState(true)
  const [searchParams] = useSearchParams()

  const searchQuery = searchParams.get('query')
  const limit = searchParams.get('pageSize')
  const page = searchParams.get('page')

  const toggleRefresh = () => setRefresh((p) => !p)

  useTitle('My Forms | Form Builder')

  const { mutate, isPending, isFetching, error } = useCreateData('/getForm', 'forms')

  useEffect(() => {
    mutate(
      { businessId: userId, search: searchQuery, page: page, limit: limit },
      {
        onSuccess: (responseData) => {
          logger.log('🟢 API returned:', responseData.data.form)
          const formData = responseData?.data?.form || []
          setForms(formData)
          setTotalEntries(responseData?.data?.total)
        },
        onError: (err) => {
          logger.error('🔴 Error creating data:', err)
          toast.error('Failed to load forms')
        },
      }
    )
  }, [userId, mutate, refresh, searchQuery, limit, page])

  // Mutation hook to change the status of the form
  const statusMutation = useMutation({
    mutationFn: ({ formId, businessId, isActive }) =>
      axiosInstance.post('/updateFormStatus', { formId, businessId, isActive }),

    onSuccess: (_, variables) => {
      const { formId } = variables
      // Update local forms state to reflect that only one form is active
      setForms((prevForms) =>
        prevForms.map((form) => ({
          ...form,
          isActive: form._id === formId, // only one stays true
        }))
      )
      toast.success('Form status updated successfully')
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating form status')
    },
  })

  const bulkDeleteMutation = useUpdateData('forms/bulk-delete', 'forms')

  const columns = useMemo(
    () => [
      {
        id: 'sno',
        header: <span className="pl-2">S.No</span>,
        cell: ({ row }) => <span className="pl-4">{row.index + 1}</span>,
        enableSorting: false,
        enableHiding: false,
      },
      // {
      //   id: "select",
      //   header: ({ table }) => (
      //     <Checkbox
      //       checked={table.getIsAllPageRowsSelected()}
      //       onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      //       aria-label="Select all"
      //     />
      //   ),
      //   cell: ({ row }) => (
      //     <Checkbox
      //       checked={row.getIsSelected()}
      //       onCheckedChange={(value) => row.toggleSelected(!!value)}
      //       aria-label="Select row"
      //       onClick={(e) => e.stopPropagation()}
      //     />
      //   ),
      //   enableSorting: false,
      //   enableHiding: false,
      // },
      {
        accessorKey: 'isActive',
        // header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
        header: () => <span>Status</span>,
        cell: ({ row }) => {
          return (
            <Switch
              checked={row.original.isActive}
              disabled={
                statusMutation.isPending && statusMutation.variables?.formId === row.original._id
              }
              onCheckedChange={(checked) => {
                statusMutation.mutate({
                  formId: row.original._id,
                  isActive: checked,
                  businessId: userId,
                })
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )
        },
      },
      {
        accessorKey: 'name',
        // header: ({ column }) => <DataTableColumnHeader column={column} title="Form Name" />,
        header: () => <span>Form Name</span>,
      },
      {
        accessorKey: 'createdAt',
        // header: ({ column }) => <DataTableColumnHeader column={column} title="Created Date" />,
        header: () => <span>Created Date</span>,
        cell: ({ row }) => <span>{dayjs(row.original.createdAt).format('MMM D, YYYY')}</span>,
      },
      {
        accessorKey: 'updatedAt',
        // header: ({ column }) => <DataTableColumnHeader column={column} title="Last Updated" />,
        header: () => <span>Last Updated</span>,
        cell: ({ row }) => <span>{dayjs(row.original.updatedAt).fromNow()}</span>,
      },
      {
        id: 'actions',
        header: () => <span>Actions</span>,
        cell: ({ row }) => (
          <DataTableRowActions refreshData={toggleRefresh} formId={row.original._id} />
        ),
      },
    ],
    [statusMutation, userId]
  )

  if (isPending) return <DataTableShimmer columns={6} />
  if (error) return <NotFound fullScreen={false} />

  return (
    <DataTable
      columns={columns}
      data={forms}
      totalEntries={totalEntries}
      isFetching={isFetching}
      bulkDeleteHandler={(selectedForms) => {
        bulkDeleteMutation.mutate(selectedForms)
      }}
      bulkDeleteIsLoading={bulkDeleteMutation.isPending}
    />
  )
}

export default MyForms
